import type { CityConfig } from "../city-template/types";

export const akronConfig: CityConfig = {
  city: "Akron",
  state: "Ohio",
  stateAbbr: "OH",
  slug: "akron",
  route: "/cities/akron",
  title: "Emergency Passport Akron, OH | Expedited Passport Services | Fast Passport Center",
  metaDescription: "Emergency and expedited passport services in Akron, OH — as fast as 24 hours. Same-day courier processing available. A+ BBB rated, registered State Dept. courier serving Summit County and Northeast Ohio. Get your passport fast!",
  h1: "Emergency & Expedited Passport Services in Akron, OH",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Akron and Summit County travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Akron%20Ohio%20downtown%20skyline%20at%20golden%20sunset%20with%20the%20beautiful%20Cuyahoga%20Valley%20in%20the%20distance%2C%20modern%20and%20historic%20architecture%20bathed%20in%20warm%20amber%20light%2C%20green%20rolling%20hills%20visible%20beyond%20the%20city%2C%20the%20Lock%203%20park%20area%20with%20urban%20revitalization%2C%20professional%20cityscape%20photography%20wide%20panoramic%20view%2C%20vibrant%20Northeast%20Ohio%20atmosphere&width=1800&height=700&seq=akron-hero-oh-001&orientation=landscape",
  heroImageAlt: "Akron Ohio downtown skyline at golden sunset with the Cuyahoga Valley",
  geoPosition: "41.0814;-81.5190",
  geoPlacename: "Akron, Ohio",
  regionalAgency: {
    name: "Chicago Passport Agency",
    address: "230 S Dearborn St, Suite 380, Chicago, IL 60604",
    distance: "Located in downtown Chicago — approximately 350 miles from Akron via I-80",
  },
  nearbyCitiesZips: [
    { name: "Akron", zips: "44301–44399" },
    { name: "Cuyahoga Falls", zips: "44221, 44222, 44223" },
    { name: "Stow", zips: "44224" },
    { name: "Hudson", zips: "44236, 44237" },
    { name: "Fairlawn", zips: "44333" },
    { name: "Bath", zips: "44210" },
    { name: "Tallmadge", zips: "44278" },
    { name: "Kent", zips: "44240, 44241, 44242" },
    { name: "Barberton", zips: "44203" },
    { name: "Wadsworth", zips: "44281, 44282" },
    { name: "Medina", zips: "44256, 44258" },
    { name: "Canton", zips: "44701–44799" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Akron departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-map-pin-2-line", title: "Northeast Ohio Hub", desc: "Serving Summit, Portage, and Medina counties" },
  ],
  internalLinks: [
    { name: "Expedited passport services in Cleveland, OH", route: "/cities/cleveland" },
    { name: "Fast passport renewal in Canton, OH", route: "/cities/canton" },
    { name: "Emergency passport help in Columbus, OH", route: "/cities/columbus-oh" },
    { name: "Urgent passport services in Cuyahoga Falls", route: "/cities/cuyahoga-falls" },
    { name: "Same-day passport help in Kent, OH", route: "/kent" },
    { name: "Passport courier services in Medina, OH", route: "/cities/medina" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Akron remotely via secure courier. We do not have a walk-in office in Akron — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Summit County and Northeast Ohio travelers.",
  fpcOfficeDistance: "Serving all of Summit County and the greater Northeast Ohio region",
  serviceAreaIntro: "From downtown Akron to the suburbs of Cuyahoga Falls, Stow, and Hudson, from Fairlawn to Kent and Barberton — Fast Passport Center serves the entire Greater Akron area. We regularly help travelers from Tallmadge, Wadsworth, and Medina get their passports fast.",
  nearbyCitiesIntro: "We serve all Akron neighborhoods and surrounding Northeast Ohio cities including Cuyahoga Falls, Stow, Hudson, Fairlawn, Bath, Tallmadge, Kent, Barberton, Wadsworth, Medina, and all the way to Canton. If you are anywhere in Summit, Portage, Medina, or Wayne counties, we have you covered.",
  howItWorksIntro: "Most Akron travelers complete everything online and ship documents overnight. No need to drive to Chicago or wait in line at the regional agency.",
  mistakesIntro: "Akron travelers often face long drives to the nearest regional passport agency. Here are the most common mistakes that cause delays.",
  testimonialsIntro: "Akron and Summit County travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Akron? Here are the most common ones we hear from Summit County travelers.",
  statePageRoute: "/states/ohio",
  statePageName: "Ohio",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Bereavement or medical crisis",
        "Expect to wait all day or return next day",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "3–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable turnaround",
        "Ideal for upcoming trips",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Good for planned travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–3",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "Workload & wait times shift with shutdowns",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "No phone or email contact for your case",
        "Can't upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent Akron area flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Kevin D.",
      location: "Akron, OH",
      rating: 5,
      text: "Emergency passport for a last-minute trip to Toronto. Got it in 36 hours. Fast Passport Center kept me updated every hour. Outstanding service.",
      service: "Emergency Passport",
    },
    {
      name: "Patricia W.",
      location: "Cuyahoga Falls, OH",
      rating: 5,
      text: "Renewed my passport before our anniversary trip to Italy. Everything done online — delivered in 4 business days. Could not have been easier.",
      service: "Passport Renewal",
    },
    {
      name: "Brian T.",
      location: "Stow, OH",
      rating: 5,
      text: "Lost my passport 3 days before a business trip. Fast Passport Center had a replacement ready in 30 hours. Absolute lifesavers.",
      service: "Lost Passport",
    },
    {
      name: "Michelle K.",
      location: "Hudson, OH",
      rating: 5,
      text: "First passport for our 14-year-old headed to Costa Rica with her school. The team walked us through every step. Delivered on time with zero stress.",
      service: "Child Passport",
    },
  ],
  facilities: [
    {
      id: "akron-1",
      name: "Akron Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "675 Wolf Ledges Pkwy",
      city: "Akron",
      state: "OH",
      zip: "44311",
      phone: "(330) 996-9630",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "South of downtown Akron",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Primary Akron facility. Appointments required. Photo services available nearby.",
      icon: "ri-mail-line",
    },
    {
      id: "akron-2",
      name: "Cuyahoga Falls Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "2885 State Rd",
      city: "Cuyahoga Falls",
      state: "OH",
      zip: "44223",
      phone: "(330) 928-2511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "North Akron suburb",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Cuyahoga Falls, Stow, and Tallmadge residents.",
      icon: "ri-mail-line",
    },
    {
      id: "akron-3",
      name: "Fairlawn Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "455 Ghent Rd",
      city: "Fairlawn",
      state: "OH",
      zip: "44333",
      phone: "(330) 668-2511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Northwest Akron suburb",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Fairlawn, Bath, and northwest Akron area. Less crowded than downtown.",
      icon: "ri-mail-line",
    },
    {
      id: "akron-4",
      name: "Kent Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "211 E Main St",
      city: "Kent",
      state: "OH",
      zip: "44240",
      phone: "(330) 673-2511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "East Akron area in Portage County",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Kent State University area and Portage County. Full passport services.",
      icon: "ri-mail-line",
    },
    {
      id: "akron-5",
      name: "Summit County Clerk of Courts",
      type: "Court Clerk Passport Acceptance",
      address: "209 S High St",
      city: "Akron",
      state: "OH",
      zip: "44308",
      phone: "(330) 643-2206",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "Downtown Akron Courthouse",
      website: "https://www.summitoh.net/clerk",
      websiteLabel: "Summit County Clerk",
      notes: "Downtown Akron location. Good alternative to post office. Appointment recommended.",
      icon: "ri-government-line",
    },
    {
      id: "akron-6",
      name: "Medina Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "340 W Liberty St",
      city: "Medina",
      state: "OH",
      zip: "44256",
      phone: "(330) 725-2511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Southwest Akron in Medina County",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Medina County and southwest Summit County residents.",
      icon: "ri-mail-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — 5+ hour drive to Chicago", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Akron photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or wrong form type are the #1 rejection reasons at Summit County acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Akron applicants arrive with incorrect payment and must reschedule.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or provide notarized consent. This is a common issue at Summit County facilities.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Akron travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Akron." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Akron address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in Akron before international travel?", a: "Yes. Fast Passport Center provides emergency passport services in Akron for travelers with urgent international flights. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities, achieving 24–48 hour turnaround without the long drive to Chicago." },
    { q: "How fast can I renew my passport in Akron?", a: "Fast Passport Center offers fast passport renewal in Akron in as little as 24–48 hours through our emergency courier service. For less urgent travel, our 3–5 day expedited option provides the best value. Both options include full document review and a dedicated agent." },
    { q: "What is the closest passport agency to Akron?", a: "The nearest regional passport agency to Akron is the Chicago Passport Agency, approximately 350 miles away via I-80. For urgent processing without the long drive, Fast Passport Center's registered courier service achieves 24–48 hour turnaround from Akron." },
    { q: "Do you serve the Cuyahoga Valley and Kent State area?", a: "Yes. Fast Passport Center serves the full Greater Akron region including Cuyahoga Falls, Stow, Hudson, Kent, Tallmadge, and the Cuyahoga Valley National Park area. Our overnight courier service works from any UPS location regardless of your specific Summit, Portage, or Medina county address." },
    { q: "Can I get a same-day passport appointment near Akron?", a: "Same-day passport appointments are only available at the Chicago Regional Passport Agency for documented life-or-death emergencies. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option for Summit County residents." },
    { q: "Do I need to drive to the Chicago Passport Agency?", a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities." },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Akron", address: "990 E Market St, Akron, OH 44305", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Cuyahoga Falls", address: "2751 State Rd, Cuyahoga Falls, OH 44223", hours: "Mon–Sun: 7AM–11PM" },
    { name: "FedEx Office — Akron", address: "3536 S Main St, Akron, OH 44319", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
    { name: "Walmart Supercenter — Fairlawn", address: "2887 W Market St, Fairlawn, OH 44333", hours: "Mon–Sun: 6AM–11PM" },
    { name: "CVS Pharmacy — Stow", address: "4408 Kent Rd, Stow, OH 44224", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Hudson", address: "5433 Darrow Rd, Hudson, OH 44236", hours: "Mon–Sun: 7AM–11PM" },
    { name: "CVS Pharmacy — Medina", address: "3443 Medina Rd, Medina, OH 44256", hours: "Mon–Sun: 8AM–10PM" },
    { name: "AAA Travel — Akron", address: "3601 Embassy Pkwy, Akron, OH 44333", hours: "Mon–Fri: 8AM–5:30PM" },
  ],
  dmvLocations: [
    {
      name: "Summit County BMV — Akron",
      address: "1030 E Tallmadge Ave, Akron, OH 44310",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Driver's license and REAL ID services. Appointment recommended.",
      featured: true,
    },
    {
      name: "Summit County BMV — Fairlawn",
      address: "2737 W Market St, Akron, OH 44333",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Northwest Akron option. Typically shorter wait times. REAL ID available.",
      featured: false,
    },
    {
      name: "Portage County BMV — Ravenna",
      address: "449 S Meridian St, Ravenna, OH 44266",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Serves Kent, Portage County area residents.",
      featured: false,
    },
    {
      name: "Medina County BMV",
      address: "210 Northland Dr, Medina, OH 44256",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Serves Medina County and southwest Summit County residents.",
      featured: true,
    },
  ],
  airports: [
    {
      code: "CAK",
      name: "Akron-Canton Airport",
      distance: "20 miles south of downtown Akron",
      address: "5400 Lauby Rd NW, North Canton, OH 44720",
      note: "Primary airport serving the Akron-Canton area. Good domestic connections with Southwest and other major carriers. Easier parking than Cleveland.",
      flightUrl: "https://www.akroncantonairport.com/",
      flightLabel: "CAK Airport",
      featured: true,
    },
    {
      code: "CLE",
      name: "Cleveland Hopkins International Airport",
      distance: "40 miles northwest of Akron",
      address: "5300 Riverside Dr, Cleveland, OH 44135",
      note: "Major international hub. United hub with direct international flights. Preferred for long-haul international travel from the Akron area.",
      flightUrl: "https://www.clevelandairport.com/",
      flightLabel: "Cleveland Airport",
      featured: false,
    },
    {
      code: "CMH",
      name: "John Glenn Columbus International Airport",
      distance: "125 miles southwest of Akron",
      address: "4600 International Gateway, Columbus, OH 43219",
      note: "Alternative hub for additional international routes. Consider for long-haul trips from the Akron-Summit County area.",
      flightUrl: "https://www.flycmh.com/",
      flightLabel: "Columbus Airport",
      featured: false,
    },
  ],
};